import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import RouteValidator from './components/RouteValidator';
import FallbackRoute from './components/FallbackRoute';

// Import pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import WebsiteChecks from './pages/WebsiteChecks';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import ChangePassword from './pages/ChangePassword';
import TeamInvitations from './pages/TeamInvitations';
import InvitationAccept from './pages/InvitationAccept';
import InvitationReject from './pages/InvitationReject';
import OrganizationSettings from './pages/OrganizationSettings';
import Waitlist from './pages/Waitlist';
import EmailVerification from './pages/EmailVerification';

import NewsletterVerification from './pages/NewsletterVerification';
import NewsletterUnsubscribe from './pages/NewsletterUnsubscribe';

// Product Pages
import ProductFeatures from './pages/Product/Features';
import ProductAPI from './pages/Product/API';
import ProductDocumentation from './pages/Product/Documentation';
import ProductPricing from './pages/Product/Pricing';

// Company Pages
import CompanyAbout from './pages/Company/About';
import CompanyCareers from './pages/Company/Careers';
import CompanyContact from './pages/Company/Contact';

// Legal Pages
import LegalPrivacy from './pages/Legal/Privacy';
import LegalTerms from './pages/Legal/Terms';
import LegalSecurity from './pages/Legal/Security';

// Admin Pages
import AdminDashboard from './pages/Admin/Dashboard';
import AdminSettings from './pages/Admin/Settings';
import AdminBlogPosts from './pages/Admin/BlogPosts';
import AdminNewsletter from './pages/Admin/Newsletter';
import AdminWaitlist from './pages/Admin/Waitlist';

import AdminUsers from './pages/Admin/Users';
import AdminOrganizations from './pages/Admin/Organizations';

// Evaluation Pages
import EvaluationsDashboard from './pages/Evaluations/Dashboard';
import APIConfiguration from './pages/Evaluations/APIConfiguration';
import EvaluationWizard from './pages/Evaluations/EvaluationWizard';
import EvaluationExecution from './pages/Evaluations/EvaluationExecution';
import RunResultsView from './pages/Evaluations/RunResultsView';
import TestAuth from './pages/TestAuth';
import ResultsVisualization from './pages/Evaluations/ResultsVisualization';
import SchedulingInterface from './pages/Evaluations/SchedulingInterface';
import WebhookInterface from './pages/Evaluations/WebhookInterface';
import ModelComparison from './pages/Evaluations/ModelComparison';

// Template Pages
import TemplateList from './pages/Templates/TemplateList';
import TemplateCreate from './pages/Templates/TemplateCreate';
import TemplateEdit from './pages/Templates/TemplateEdit';
import TemplateView from './pages/Templates/TemplateView';

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <div className="flex flex-col min-h-screen">
            <Header />
            <main className="flex-grow">
              <RouteValidator>
                <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/email/verification" element={<EmailVerification />} />
              <Route path="/email/verify" element={<Navigate to="/email/verification" replace />} />
              <Route path="/email/verify/*" element={<Navigate to="/email/verification" replace />} />
              <Route path="/newsletter/verification" element={<NewsletterVerification />} />
              <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
              <Route path="/waitlist" element={<Waitlist />} />

              {/* Product Routes */}
              <Route path="/product/features" element={<ProductFeatures />} />
              <Route path="/product/api" element={<ProductAPI />} />
              <Route path="/product/documentation" element={<ProductDocumentation />} />
              <Route path="/product/pricing" element={<ProductPricing />} />

              {/* Company Routes */}
              <Route path="/company/about" element={<CompanyAbout />} />
              <Route path="/company/careers" element={<CompanyCareers />} />
              <Route path="/company/contact" element={<CompanyContact />} />

              {/* Legal Routes */}
              <Route path="/legal/privacy" element={<LegalPrivacy />} />
              <Route path="/legal/terms" element={<LegalTerms />} />
              <Route path="/legal/security" element={<LegalSecurity />} />

              {/* Blog Routes */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />

              {/* User Routes */}
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/profile/password" element={<ChangePassword />} />
              <Route path="/organization-settings" element={<OrganizationSettings />} />
              <Route path="/profile/organization" element={<OrganizationSettings />} />
              <Route path="/website-checks" element={<WebsiteChecks />} />

              {/* Team Routes */}
              <Route path="/team/invitations" element={<TeamInvitations />} />
              <Route path="/team-invitations" element={<TeamInvitations />} />
              <Route path="/team/invitation/accept" element={<InvitationAccept />} />
              <Route path="/team/invitation/reject" element={<InvitationReject />} />
              <Route path="/invite/accept/:token" element={<InvitationAccept />} />
              <Route path="/invite/reject/:token" element={<InvitationReject />} />

              {/* Evaluation Routes */}
              <Route path="/evaluations" element={<EvaluationsDashboard />} />
              <Route path="/evaluations/dashboard" element={<EvaluationsDashboard />} />
              <Route path="/evaluations/api-config" element={<APIConfiguration />} />
              <Route path="/test-auth" element={<TestAuth />} />
              <Route path="/evaluations/results" element={<ResultsVisualization />} />
              <Route path="/evaluations/scheduling" element={<SchedulingInterface />} />
              <Route path="/evaluations/webhooks" element={<WebhookInterface />} />
              <Route path="/evaluations/wizard" element={<EvaluationWizard />} />
              <Route path="/evaluations/:id/execute" element={<EvaluationExecution />} />
              <Route path="/evaluations/runs/:runId" element={<RunResultsView />} />

              <Route path="/model-comparisons" element={<ModelComparison />} />
              <Route path="/model-comparisons/:id" element={<ModelComparison />} />

              {/* Template Routes */}
              <Route path="/templates" element={<TemplateList />} />
              <Route path="/templates/create" element={<TemplateCreate />} />
              <Route path="/templates/:id/edit" element={<TemplateEdit />} />
              <Route path="/templates/:id" element={<TemplateView />} />

              <Route path="/schedules" element={<Navigate to="/evaluations/scheduling" replace />} />
              <Route path="/api-config" element={<Navigate to="/evaluations/api-config" replace />} />
              <Route path="/webhooks" element={<Navigate to="/evaluations/webhooks" replace />} />
              <Route path="/results" element={<Navigate to="/evaluations/results" replace />} />
              <Route path="/comparison" element={<Navigate to="/model-comparisons" replace />} />
              <Route path="/admin/templates" element={<Navigate to="/admin" replace />} />
              <Route path="/admin/analytics" element={<Navigate to="/admin" replace />} />

              {/* Admin Routes */}
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/blog-posts" element={<AdminBlogPosts />} />
              <Route path="/admin/newsletter" element={<AdminNewsletter />} />
              <Route path="/admin/waitlist" element={<AdminWaitlist />} />

              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/organizations" element={<AdminOrganizations />} />

              {/* 404 Catch-all route with enhanced fallback */}
              <Route path="*" element={<FallbackRoute />} />
            </Routes>
              </RouteValidator>
            </main>
            <Footer />
          </div>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                theme: {
                  primary: 'green',
                  secondary: 'black',
                },
              },
            }}
          />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
